/**
 * src/infra/sync/createStore.ts
 *
 * Lightweight store factory for Pioneer Marketplace.
 * Provides sessionStorage caching + subscribe() for useSyncExternalStore.
 * Simplified from Pioneer core — no IndexedDB, no BroadcastChannel (single app).
 */

export interface StoreConfig<T> {
  cacheKey: string;
  initialValue: T;
  ttlMs?: number;
}

export interface Store<T> {
  getSnapshot(): T;
  subscribe(listener: () => void): () => void;
  setState(nextOrUpdater: T | ((prev: T) => T)): void;
  patchState(partial: Partial<T>): void;
  isStale(): boolean;
  clear(): void;
}

interface CacheEnvelope<T> {
  data: T;
  lastUpdated: number;
}

const DEFAULT_TTL_MS = 5 * 60 * 1000;

function tryParseJson<T>(raw: string | null): T | null {
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function createStore<T extends object>(config: StoreConfig<T>): Store<T> {
  const { cacheKey, initialValue, ttlMs = DEFAULT_TTL_MS } = config;

  let state: T = initialValue;
  let lastUpdated = 0;
  const listeners = new Set<() => void>();

  // Hydrate from sessionStorage
  const raw = sessionStorage.getItem(cacheKey);
  if (raw !== null) {
    const envelope = tryParseJson<CacheEnvelope<T>>(raw);
    if (envelope && typeof envelope === 'object' && 'data' in envelope) {
      state = envelope.data;
      lastUpdated = envelope.lastUpdated;
    }
  }

  function persist(): void {
    const envelope: CacheEnvelope<T> = { data: state, lastUpdated };
    sessionStorage.setItem(cacheKey, JSON.stringify(envelope));
  }

  function notify(): void {
    listeners.forEach((fn) => fn());
  }

  return {
    getSnapshot(): T {
      return state;
    },

    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    setState(nextOrUpdater: T | ((prev: T) => T)): void {
      state =
        typeof nextOrUpdater === 'function'
          ? (nextOrUpdater as (prev: T) => T)(state)
          : nextOrUpdater;
      lastUpdated = Date.now();
      persist();
      notify();
    },

    patchState(partial: Partial<T>): void {
      state = { ...state, ...partial };
      lastUpdated = Date.now();
      persist();
      notify();
    },

    isStale(): boolean {
      return Date.now() - lastUpdated > ttlMs;
    },

    clear(): void {
      state = initialValue;
      lastUpdated = 0;
      sessionStorage.removeItem(cacheKey);
      notify();
    },
  };
}
